import React, { useEffect, useState } from 'react';
import MasterLayout from '../masterLayout/MasterLayout';
import { Button, Row, Col, Card, Form } from 'react-bootstrap';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Icon } from '@iconify/react';
import { API_URL } from '..';
import { useParams, useNavigate } from 'react-router-dom';

const EditTruckExpenseLog = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    truck_id: '',
    category_title: '',
    expense_title: '',
    expense_details: '',
    amount: '',
    date: '',
    payment_method: '',
    units: '',
    odometer: '',
    attachment: null,
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user) {
      window.location.href = '/sign-in';
    } else {
      fetchTruckExpense();
    }
  }, []);

  const fetchTruckExpense = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/truck/single-expense/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
        },
      });
      if (response.status === 200) {
        const data = response.data.truckExpenses;
        setFormData({
          truck_id: data.truck_id || '',
          category_title: data.category_title || '',
          expense_title: data.expense_title || '',
          expense_details: data.expense_details || '',
          amount: data.amount || '',
          date: data.date ? data.date.split('T')[0] : '',
          payment_method: data.payment_method || '',
          units: data.units || '',
          odometer: data.odometer || '',
          attachment: null, // File input starts empty; existing file is handled separately
        });
      } else {
        Swal.fire('Error', response.data.message || 'Failed to fetch truck expense', 'error');
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch truck expense: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, attachment: e.target.files[0] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.expense_title || !formData.amount) {
      Swal.fire('Error', 'Expense Title and Amount are required', 'error');
      return;
    }

    setSubmitting(true);
    const data = new FormData();
    data.append('truck_id', formData.truck_id);
    data.append('category_title', formData.category_title);
    data.append('expense_title', formData.expense_title);
    data.append('expense_details', formData.expense_details);
    data.append('amount', formData.amount);
    data.append('date', formData.date);
    data.append('payment_method', formData.payment_method);
    data.append('units', formData.units);
    data.append('odometer', formData.odometer);
    if (formData.attachment) {
      data.append('attachment', formData.attachment);
    }

    try {
      const response = await axios.put(`${API_URL}/api/truck-expense/${id}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Accept: '*/*',
        },
      });
      if (response.status === 200) {
        Swal.fire('Success', 'Truck expense updated successfully', 'success').then(() => {
          navigate('/expense');
        });
      } else {
        Swal.fire('Error', response.data.message || 'Failed to update truck expense', 'error');
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to update truck expense: ' + error.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MasterLayout>
      <div className="container py-5">
        <Row className="justify-content-center">
          <Col lg={8}>
            <Card className="p-3 border-0">
              <Card.Header className="bg-white border-bottom">
                <h6 className="h6 mb-0">Edit Truck Expense</h6>
              </Card.Header>
              <Card.Body className="p-4">
                {loading ? (
                  <p>Loading...</p>
                ) : (
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Truck ID</Form.Label>
                          <Form.Control
                            type="number"
                            name="truck_id"
                            value={formData.truck_id}
                            onChange={handleInputChange}
                            placeholder="Enter Truck ID"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Category Title</Form.Label>
                          <Form.Control
                            type="text"
                            name="category_title"
                            value={formData.category_title}
                            onChange={handleInputChange}
                            placeholder="Enter Category Title"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Expense Title</Form.Label>
                          <Form.Control
                            type="text"
                            name="expense_title"
                            value={formData.expense_title}
                            onChange={handleInputChange}
                            placeholder="Enter Expense Title"
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Expense Details</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="expense_details"
                            value={formData.expense_details}
                            onChange={handleInputChange}
                            placeholder="Enter Expense Details"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Amount</Form.Label>
                          <Form.Control
                            type="number"
                            step="0.01"
                            name="amount"
                            value={formData.amount}
                            onChange={handleInputChange}
                            placeholder="Enter Amount"
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Date</Form.Label>
                          <Form.Control
                            type="date"
                            name="date"
                            value={formData.date}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Payment Method</Form.Label>
                          <Form.Select
                            name="payment_method"
                            value={formData.payment_method}
                            onChange={handleInputChange}
                          >
                            <option value="">Select Payment Method</option>
                            <option value="cash">Cash</option>
                            <option value="card">Card</option>
                            <option value="check">Check</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Units</Form.Label>
                          <Form.Control
                            type="text"
                            name="units"
                            value={formData.units}
                            onChange={handleInputChange}
                            placeholder="Enter Units"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Odometer</Form.Label>
                          <Form.Control
                            type="text"
                            name="odometer"
                            value={formData.odometer}
                            onChange={handleInputChange}
                            placeholder="Enter Odometer"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Attachment</Form.Label>
                          <Form.Control
                            type="file"
                            name="attachment"
                            onChange={handleFileChange}
                            accept=".pdf,.jpg,.jpeg,.png"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="d-flex justify-content-end mt-4">
                      <Button
                        variant="secondary"
                        className="me-2"
                        onClick={() => navigate('/expense')}
                        disabled={submitting}
                      >
                        <Icon icon="bi:x-circle" className="me-2" />
                        Cancel
                      </Button>
                      <Button type="submit" variant="primary" disabled={submitting}>
                        <Icon icon="bi:check-circle" className="me-2" />
                        {submitting ? 'Updating...' : 'Update Expense'}
                      </Button>
                    </div>
                  </Form>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </MasterLayout>
  );
};

export default EditTruckExpenseLog;